import { cn } from '@/lib/utils';
import { PANEL } from '@/lib/surface';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { PageHeader } from '@/components/common';
import {
  EquipmentCategory,
  EquipmentStatus,
  EquipmentCondition,
} from '@/api/services/equipment.api';
import { useUpdateEquipment, useLocationsList } from '@/api/hooks';
import { equipmentApi } from '@/api/services';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';

const categories: EquipmentCategory[] = [
  'DRILLING',
  'COMPLETION',
  'PRODUCTION',
  'TESTING',
  'SAFETY',
  'TRANSPORTATION',
  'OTHER',
];
const statuses: EquipmentStatus[] = [
  'AVAILABLE',
  'IN_USE',
  'MAINTENANCE',
  'REPAIR',
  'RETIRED',
  'DISPOSED',
];
const conditions: EquipmentCondition[] = ['EXCELLENT', 'GOOD', 'FAIR', 'POOR', 'CRITICAL'];

interface EquipmentFormData {
  assetNumber: string;
  name: string;
  description: string;
  category: EquipmentCategory;
  manufacturer: string;
  model: string;
  serialNumber: string;
  locationId: string;
  status: EquipmentStatus;
  condition: EquipmentCondition;
  purchaseDate: string;
  purchasePrice: string;
  warrantyExpiry: string;
  operatingHours: string;
}

export default function EquipmentEdit() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState<EquipmentFormData>({
    assetNumber: '',
    name: '',
    description: '',
    category: 'OTHER',
    manufacturer: '',
    model: '',
    serialNumber: '',
    locationId: '',
    status: 'AVAILABLE',
    condition: 'GOOD',
    purchaseDate: '',
    purchasePrice: '',
    warrantyExpiry: '',
    operatingHours: '',
  });

  const updateMutation = useUpdateEquipment();
  const { data: locationsData } = useLocationsList({ limit: 500 });
  const locations = locationsData?.data || [];

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      try {
        const res = await equipmentApi.getById(id);
        const eq = res.data;
        setFormData({
          assetNumber: eq.assetNumber || '',
          name: eq.name || '',
          description: eq.description || '',
          category: eq.category || 'OTHER',
          manufacturer: eq.manufacturer || '',
          model: eq.model || '',
          serialNumber: eq.serialNumber || '',
          locationId: eq.locationId || '',
          status: eq.status || 'AVAILABLE',
          condition: eq.condition || 'GOOD',
          purchaseDate: eq.purchaseDate ? eq.purchaseDate.split('T')[0] : '',
          purchasePrice: eq.purchasePrice != null ? String(eq.purchasePrice) : '',
          warrantyExpiry: eq.warrantyExpiry ? eq.warrantyExpiry.split('T')[0] : '',
          operatingHours: eq.operatingHours != null ? String(eq.operatingHours) : '',
        });
      } catch {
        toast.error('Failed to load equipment');
        navigate('/equipment');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.assetNumber || !formData.name || !formData.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    const payload = {
      assetNumber: formData.assetNumber,
      name: formData.name,
      description: formData.description || undefined,
      category: formData.category,
      manufacturer: formData.manufacturer || undefined,
      model: formData.model || undefined,
      serialNumber: formData.serialNumber || undefined,
      locationId: formData.locationId || undefined,
      status: formData.status,
      condition: formData.condition,
      purchaseDate: formData.purchaseDate || undefined,
      purchasePrice: formData.purchasePrice ? parseFloat(formData.purchasePrice) : undefined,
      warrantyExpiry: formData.warrantyExpiry || undefined,
      operatingHours: formData.operatingHours ? parseFloat(formData.operatingHours) : undefined,
    };

    updateMutation.mutate(
      { id: id!, data: payload },
      {
        onSuccess: () => {
          toast.success('Equipment updated successfully');
          navigate(`/equipment/${id}`);
        },
      }
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <motion.div variants={stagger} initial="initial" animate="animate" className="space-y-6">
      <PageHeader
        breadcrumbs={[
          { label: 'Home', href: '/' },
          { label: 'Equipment', href: '/equipment' },
          { label: 'Edit' },
        ]}
        title="Edit Equipment"
        description="Amendments to the recorded details of an existing asset."
        actions={
          <Button variant="outline" onClick={() => navigate(`/equipment/${id}`)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Equipment
          </Button>
        }
      />

      <motion.div variants={fadeUp}>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Basic Information</h3>
            <Separator />

            <div className="space-y-2">
              <Label htmlFor="assetNumber">Asset Number *</Label>
              <Input
                id="assetNumber"
                value={formData.assetNumber}
                onChange={(e) =>
                  setFormData({ ...formData, assetNumber: e.target.value.toUpperCase() })
                }
                placeholder="EQ-001"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="category">Category *</Label>
              <Select
                value={formData.category}
                onValueChange={(v: EquipmentCategory) => setFormData({ ...formData, category: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Top Drive Unit"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Equipment description..."
                rows={3}
              />
            </div>
          </div>

          {/* Details */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Details</h3>
            <Separator />

            <div className="space-y-2">
              <Label htmlFor="manufacturer">Manufacturer</Label>
              <Input
                id="manufacturer"
                value={formData.manufacturer}
                onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                placeholder="National Oilwell Varco"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="model">Model</Label>
              <Input
                id="model"
                value={formData.model}
                onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                placeholder="Model number"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="serialNumber">Serial Number</Label>
              <Input
                id="serialNumber"
                value={formData.serialNumber}
                onChange={(e) => setFormData({ ...formData, serialNumber: e.target.value })}
                placeholder="SN-XXXXXXXXX"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="locationId">Location</Label>
              <Select
                value={formData.locationId}
                onValueChange={(v) => setFormData({ ...formData, locationId: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select location..." />
                </SelectTrigger>
                <SelectContent>
                  {Array.isArray(locations) &&
                    locations.map((loc: { id: string; name: string }) => (
                      <SelectItem key={loc.id} value={loc.id}>
                        {loc.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Status & Condition */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Status &amp; Condition</h3>
            <Separator />

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(v: EquipmentStatus) => setFormData({ ...formData, status: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statuses.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="condition">Condition</Label>
              <Select
                value={formData.condition}
                onValueChange={(v: EquipmentCondition) =>
                  setFormData({ ...formData, condition: v })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {conditions.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Purchase Information */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Purchase Information</h3>
            <Separator />

            <div className="space-y-2">
              <Label htmlFor="purchaseDate">Purchase Date</Label>
              <Input
                id="purchaseDate"
                type="date"
                value={formData.purchaseDate}
                onChange={(e) => setFormData({ ...formData, purchaseDate: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="purchasePrice">Purchase Price ($)</Label>
              <Input
                id="purchasePrice"
                type="number"
                step="0.01"
                value={formData.purchasePrice}
                onChange={(e) => setFormData({ ...formData, purchasePrice: e.target.value })}
                placeholder="0.00"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="warrantyExpiry">Warranty Expiry</Label>
              <Input
                id="warrantyExpiry"
                type="date"
                value={formData.warrantyExpiry}
                onChange={(e) => setFormData({ ...formData, warrantyExpiry: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="operatingHours">Operating Hours</Label>
              <Input
                id="operatingHours"
                type="number"
                step="0.1"
                value={formData.operatingHours}
                onChange={(e) => setFormData({ ...formData, operatingHours: e.target.value })}
                placeholder="0"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => navigate(`/equipment/${id}`)}>
              Cancel
            </Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
